import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputBase,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { DataGridPro, gridClasses, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import { styled, alpha } from "@mui/material/styles";
import chroma from "chroma-js";
import { useAuth, API_BASE } from "./AuthContext";
import { hitter_columns, pitcher_columns, stuff_columns, withNumericFilters } from "./utils/ColumnDefs";
import { useHeaderFilters } from "./utils/useHeaderFilters";

const ACCENT = "#3D5A80";
const DARK = "#293241";
const SEASONS = ["2026", "2025", "2024", "2023"];

// Identity columns always shown in the grid — excluded from picker
const ALWAYS_HITTER = new Set(["name", "MLB_AbbName", "Position"]);
const ALWAYS_PITCHER = new Set(["name", "MLB_AbbName", "role"]);

// ── Extra agg-level columns ────────────────────────────────────────────────
// The newer minor-league tiers (AA, A+, non-FSL A, Rookie) carry FanGraphs
// aggregate stats that don't have Statcast equivalents. These columns aren't in
// the shared ColumnDefs (which drive the main leaderboard), so define them here
// to keep them available only inside Custom Leaderboards.
const _lbScale = chroma.scale(["rgba(0,0,255,0.5)", "#C2C2C2", "rgba(255,0,0,0.5)"]);

const _coloredCell =
  (rawField: string, fmt: (v: number) => string) =>
  (params: GridRenderCellParams) => {
    const pct = params.value;
    const raw = params.row[rawField];
    // Players at levels without this stat get a blank, uncolored cell.
    if (pct == null || raw == null || isNaN(Number(raw))) return <span />;
    return (
      <Box
        sx={{
          backgroundColor: _lbScale(Number(pct)).toString(),
          width: "100%",
          height: "100%",
          textAlign: "center",
        }}
      >
        {fmt(Number(raw))}
      </Box>
    );
  };

const EXTRA_HITTER_COLS: GridColDef<any>[] = [
  {
    field: "wrcplus_p",
    headerName: "wRC+",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("wrcplus", (v) => v.toFixed(0)),
  },
  {
    field: "iso_p",
    headerName: "ISO",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("iso", (v) => v.toFixed(3).replace(/^0/, "")),
  },
  { field: "ldrate", headerName: "LD%", width: 90 },
  { field: "fbrate", headerName: "FB%", width: 90 },
  { field: "singles", headerName: "1B", width: 90 },
];

const EXTRA_PITCHER_COLS: GridColDef<any>[] = [
  {
    field: "kbb_p",
    headerName: "K-BB%",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("kbb", (v) => v.toFixed(1)),
  },
  {
    field: "fip_p",
    headerName: "FIP",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("fip", (v) => v.toFixed(2)),
  },
  {
    field: "era_p",
    headerName: "ERA",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("era", (v) => v.toFixed(2)),
  },
  {
    field: "xfip_p",
    headerName: "xFIP",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("xfip", (v) => v.toFixed(2)),
  },
  {
    field: "whip_p",
    headerName: "WHIP",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("whip", (v) => v.toFixed(2)),
  },
  {
    field: "hr9_p",
    headerName: "HR/9",
    width: 90,
    cellClassName: () => "super-app",
    renderCell: _coloredCell("hr9", (v) => v.toFixed(2)),
  },
  { field: "tbf", headerName: "TBF", width: 80 },
  { field: "ldrate", headerName: "LD%", width: 90 },
  { field: "fbrate", headerName: "FB%", width: 90 },
];

// Wrap a column's renderCell so a missing/invalid value (e.g. a Statcast stat
// for an AA player) renders a blank cell instead of throwing and crashing the
// whole grid.
const _blankOnError = (col: GridColDef<any>): GridColDef<any> => {
  const out: GridColDef<any> = { ...col };
  if (col.renderCell) {
    const orig = col.renderCell;
    out.renderCell = (params: GridRenderCellParams) => {
      const v = params.value;
      if (v === null || v === undefined || v === "") return <span />;
      try {
        return orig(params as any);
      } catch {
        return <span />;
      }
    };
  }
  // Some columns (e.g. psStuffAgg) use valueFormatter and call .toFixed on a
  // value that can be null for agg-level / no-data players — guard that too.
  if (col.valueFormatter) {
    const origFmt = col.valueFormatter;
    out.valueFormatter = (...args: any[]) => {
      const v = args[0];
      if (v === null || v === undefined || v === "") return "";
      try {
        return (origFmt as any)(...args);
      } catch {
        return "";
      }
    };
  }
  return out;
};

// Merge psStuff fields from stuff_columns into the pitcher column pool
const _STUFF_ONLY_FIELDS = new Set(["name", "MLB_AbbName", "role", "total_pitches"]);
const _psStuffCols = stuff_columns.filter((c) => !_STUFF_ONLY_FIELDS.has(c.field));
const pitcher_columns_extended = [...pitcher_columns, ..._psStuffCols];

// Full pools (shared defs + the extra agg-level columns) used for both the
// column picker catalog and grid rendering inside Custom Leaderboards.
const hitter_columns_all = [...hitter_columns, ...withNumericFilters(EXTRA_HITTER_COLS)];
const pitcher_columns_all = [
  ...pitcher_columns_extended,
  ...withNumericFilters(EXTRA_PITCHER_COLS),
];

// If a _p (colored percentile) version of a column exists, hide the raw duplicate
// so the picker only ever shows one entry per stat.
const _EXCLUDED_FIELDS = new Set(["power_agg", "d_agg", "p_agg"]);

const _hitterFields = new Set(hitter_columns_all.map((c) => c.field));
const HITTER_CATALOG = hitter_columns_all
  .filter((c) => !ALWAYS_HITTER.has(c.field))
  .filter((c) => !_hitterFields.has(`${c.field}_p`))
  .filter((c) => !_EXCLUDED_FIELDS.has(c.field))
  .map((c) => ({ field: c.field, label: c.headerName ?? c.field }));

const _pitcherFields = new Set(pitcher_columns_all.map((c) => c.field));
const PITCHER_CATALOG = pitcher_columns_all
  .filter((c) => !ALWAYS_PITCHER.has(c.field))
  .filter((c) => !_pitcherFields.has(`${c.field}_p`))
  .filter((c) => !_EXCLUDED_FIELDS.has(c.field))
  .map((c) => ({ field: c.field, label: c.headerName ?? c.field }));

interface LBSummary {
  id: number;
  name: string;
  player_type: "hitter" | "pitcher";
  season: string;
  player_ids: (number | string)[];
  columns: string[];
  created_at: string;
  updated_at: string;
}

interface LBFull extends LBSummary {
  rows: Record<string, any>[];
}

interface PlayerResult {
  id: number;
  name: string;
  Position: string;
  MLB_AbbName: string | null;
  seasons?: number[];
  levels_by_season?: Record<string, string[]>;
}

interface SelectedPlayer {
  id: number;
  name: string;
  level: string;
}

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY) },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "white",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY) },
  },
}));

export default function CustomLeaderboard({ prod }: { prod?: boolean }) {
  const { user, token } = useAuth();
  const apiBase = prod ? "https://oriolebird.pythonanywhere.com" : API_BASE;
  const authHeaders: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};

  // ── View mode ─────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<"list" | "create" | "edit" | "view">("list");

  // ── List state ───────────────────────────────────────────────────────────
  const [leaderboards, setLeaderboards] = useState<LBSummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  // ── View state ───────────────────────────────────────────────────────────
  const [activeLb, setActiveLb] = useState<LBFull | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");
  const { headerFilters, setHeaderFilters } = useHeaderFilters();

  // ── Form state ────────────────────────────────────────────────────────────
  const [formName, setFormName] = useState("");
  const [formPlayerType, setFormPlayerType] = useState<"hitter" | "pitcher">("hitter");
  const [formSeason, setFormSeason] = useState("2026");
  const [formPlayers, setFormPlayers] = useState<SelectedPlayer[]>([]);
  const [formColumns, setFormColumns] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // ── Delete confirm ────────────────────────────────────────────────────────
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Player search ─────────────────────────────────────────────────────────
  const [playerSearch, setPlayerSearch] = useState("");
  const [playerResults, setPlayerResults] = useState<PlayerResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchAbort = useRef<AbortController | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  // Level chooser shown when a searched player has rows at multiple levels.
  const [levelChoices, setLevelChoices] = useState<{ player: PlayerResult; levels: string[] } | null>(null);

  // ── Column filter ─────────────────────────────────────────────────────────
  const [colFilter, setColFilter] = useState("");

  // ── Bulk add (whole level / organization) ─────────────────────────────────
  const [bulkLevels, setBulkLevels] = useState<string[]>([]);
  const [bulkOrgs, setBulkOrgs] = useState<string[]>([]);
  const [bulkLevel, setBulkLevel] = useState("");
  const [bulkOrg, setBulkOrg] = useState("");
  const [bulkLoading, setBulkLoading] = useState<"" | "level" | "org">("");
  const [bulkMsg, setBulkMsg] = useState("");

  // ── Load leaderboard list ─────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError("");
    try {
      const res = await fetch(`${apiBase}/plus/custom-leaderboards`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setLeaderboards(await res.json());
    } catch (e: any) {
      setListError(e.message ?? "Failed to load leaderboards");
    } finally {
      setListLoading(false);
    }
  }, [apiBase, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadList();
  }, [loadList]);

  // ── Close player search on outside click ──────────────────────────────────
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
        setLevelChoices(null);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ── Debounced player search ───────────────────────────────────────────────
  useEffect(() => {
    if (!playerSearch.trim()) {
      setPlayerResults([]);
      setSearchOpen(false);
      return;
    }
    setLevelChoices(null);
    if (searchAbort.current) searchAbort.current.abort();
    searchAbort.current = new AbortController();
    const ctrl = searchAbort.current;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetch(`${apiBase}/search-fuzzy/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(playerSearch),
        signal: ctrl.signal,
      })
        .then((r) => r.json())
        .then((data: PlayerResult[]) => {
          if (Array.isArray(data)) {
            const PITCHER_POSITIONS = new Set(["SP", "RP", "P", "CL"]);
            const filtered = data.filter(
              (p) =>
                (formPlayerType === "pitcher"
                  ? PITCHER_POSITIONS.has(p.Position)
                  : !PITCHER_POSITIONS.has(p.Position)) &&
                (!p.seasons || p.seasons.length === 0 || p.seasons.includes(Number(formSeason)))
            );
            setPlayerResults(filtered.slice(0, 10));
            setSearchOpen(filtered.length > 0);
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") console.error(err);
        });
    }, 150);
  }, [playerSearch, apiBase, formSeason, formPlayerType]);

  // ── Load level / organization options for bulk add ────────────────────────
  useEffect(() => {
    if (mode !== "create" && mode !== "edit") return;
    let cancelled = false;
    setBulkLevel("");
    setBulkOrg("");
    setBulkMsg("");
    fetch(
      `${apiBase}/plus/custom-leaderboards/filter-options?player_type=${formPlayerType}&season=${formSeason}`,
      { headers: authHeaders }
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setBulkLevels(Array.isArray(data?.levels) ? data.levels : []);
        setBulkOrgs(Array.isArray(data?.orgs) ? data.orgs : []);
      })
      .catch(() => {
        if (!cancelled) {
          setBulkLevels([]);
          setBulkOrgs([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [mode, formSeason, formPlayerType, apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Player add / remove ───────────────────────────────────────────────────
  const entryKey = (id: number, level: string) => `${id}|${level}`;

  const addEntry = (p: PlayerResult, level: string) => {
    setFormPlayers((prev) =>
      prev.some((fp) => entryKey(fp.id, fp.level) === entryKey(p.id, level))
        ? prev
        : [...prev, { id: p.id, name: p.name, level }]
    );
  };

  const closeSearch = () => {
    setPlayerSearch("");
    setPlayerResults([]);
    setSearchOpen(false);
    setLevelChoices(null);
  };

  // Clicking a search result: the search response already carries the levels
  // each player has per season, so no extra request is needed. If the player
  // was at one level (or none), add directly; otherwise show a chooser so each
  // level can be added as a separate entry.
  const addPlayer = (p: PlayerResult) => {
    const levels = (p.levels_by_season && p.levels_by_season[String(formSeason)]) || [];
    if (levels.length <= 1) {
      addEntry(p, levels[0] ?? "");
      closeSearch();
    } else {
      setLevelChoices({ player: p, levels });
      setSearchOpen(false);
    }
  };

  const addLevelChoice = (level: string) => {
    if (!levelChoices) return;
    addEntry(levelChoices.player, level);
    const remaining = levelChoices.levels.filter((l) => l !== level);
    if (remaining.length === 0) {
      closeSearch();
    } else {
      setLevelChoices({ ...levelChoices, levels: remaining });
    }
  };

  const removePlayer = (key: string) => {
    setFormPlayers((prev) => prev.filter((p) => entryKey(p.id, p.level) !== key));
  };

  // ── Bulk add: every player from a level or organization ───────────────────
  const LB_MAX = 200; // matches backend _sanitize_lb_player_ids cap
  const addBulk = async (kind: "level" | "org", value: string) => {
    if (!value) return;
    setBulkLoading(kind);
    setBulkMsg("");
    try {
      const qs = new URLSearchParams({
        player_type: formPlayerType,
        season: formSeason,
        [kind]: value,
      });
      const res = await fetch(
        `${apiBase}/plus/custom-leaderboards/bulk-players?${qs.toString()}`,
        { headers: authHeaders }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error("Bad response");
      let added = 0;
      let capped = false;
      setFormPlayers((prev) => {
        const existing = new Set(prev.map((p) => entryKey(p.id, p.level)));
        const merged = [...prev];
        for (const d of data) {
          const entry = { id: Number(d.id), name: String(d.name ?? ""), level: String(d.level ?? "") };
          const key = entryKey(entry.id, entry.level);
          if (existing.has(key)) continue;
          if (merged.length >= LB_MAX) {
            capped = true;
            break;
          }
          existing.add(key);
          merged.push(entry);
          added++;
        }
        return merged;
      });
      const label = kind === "level" ? `${value}` : `${value}`;
      setBulkMsg(
        added === 0
          ? `No new players to add from ${label}.`
          : `Added ${added} player${added !== 1 ? "s" : ""} from ${label}.${
              capped ? ` Reached the ${LB_MAX}-player limit.` : ""
            }`
      );
    } catch (_) {
      setBulkMsg("Failed to add players. Try again.");
    } finally {
      setBulkLoading("");
    }
  };

  // ── Column toggle ─────────────────────────────────────────────────────────
  const toggleCol = (field: string) => {
    setFormColumns((prev) =>
      prev.includes(field) ? prev.filter((c) => c !== field) : [...prev, field]
    );
  };

  // ── Load single leaderboard with rows ─────────────────────────────────────
  const loadLbData = async (lbId: number): Promise<LBFull | null> => {
    setViewLoading(true);
    setViewError("");
    try {
      const res = await fetch(`${apiBase}/plus/custom-leaderboards/${lbId}`, {
        headers: authHeaders,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const data: LBFull = await res.json();
      setActiveLb(data);
      return data;
    } catch (e: any) {
      setViewError(e.message ?? "Failed to load leaderboard");
      return null;
    } finally {
      setViewLoading(false);
    }
  };

  // ── Open view ─────────────────────────────────────────────────────────────
  const openView = async (lb: LBSummary) => {
    setMode("view");
    await loadLbData(lb.id);
  };

  // ── Open create form ──────────────────────────────────────────────────────
  const openCreate = () => {
    setFormName("");
    setFormPlayerType("hitter");
    setFormSeason("2026");
    setFormPlayers([]);
    setFormColumns([]);
    setColFilter("");
    setSaveError("");
    setActiveLb(null);
    setLevelChoices(null);
    setBulkLevel("");
    setBulkOrg("");
    setBulkMsg("");
    setMode("create");
  };

  // ── Open edit form (fetches data to get player names) ─────────────────────
  const openEdit = async (lb: LBSummary) => {
    setViewLoading(true);
    const full = await loadLbData(lb.id);
    if (!full) return;
    setFormName(full.name);
    setFormPlayerType(full.player_type);
    setFormSeason(full.season);
    setFormColumns(full.columns);
    setColFilter("");
    // Rebuild the selected-player chips from the returned rows. Each row is one
    // (player, level) entry; this also normalizes legacy all-level boards.
    setFormPlayers(
      full.rows.map((r) => ({
        id: Number(r.id),
        name: r.name ?? `Player #${r.id}`,
        level: r.level !== undefined && r.level !== null ? String(r.level) : "",
      }))
    );
    setSaveError("");
    setMode("edit");
  };

  // ── Save (create or update) ───────────────────────────────────────────────
  const handleSave = async () => {
    if (!formName.trim()) {
      setSaveError("Name is required");
      return;
    }
    setSaving(true);
    setSaveError("");
    const body = {
      name: formName.trim(),
      player_type: formPlayerType,
      season: formSeason,
      player_ids: formPlayers.map((p) => (p.level ? `${p.id}|${p.level}` : String(p.id))),
      columns: formColumns,
    };
    try {
      const isEdit = mode === "edit" && activeLb;
      const url = isEdit
        ? `${apiBase}/plus/custom-leaderboards/${activeLb!.id}`
        : `${apiBase}/plus/custom-leaderboards`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json", ...authHeaders },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to save");
      }
      await loadList();
      setMode("list");
    } catch (e: any) {
      setSaveError(e.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (lbId: number) => {
    setDeleting(true);
    try {
      const res = await fetch(`${apiBase}/plus/custom-leaderboards/${lbId}`, {
        method: "DELETE",
        headers: authHeaders,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to delete");
      }
      await loadList();
      setDeleteConfirmId(null);
      if (mode !== "list") setMode("list");
    } catch (_) {
      /* silently ignore */
    } finally {
      setDeleting(false);
    }
  };

  // ── Build DataGridPro columns from selected fields ────────────────────────
  const buildGridColumns = () => {
    if (!activeLb) return [];
    const catalog = activeLb.player_type === "hitter" ? hitter_columns_all : pitcher_columns_all;
    const always = activeLb.player_type === "hitter" ? ALWAYS_HITTER : ALWAYS_PITCHER;
    const alwaysCols = catalog.filter((c) => always.has(c.field));
    const levelCol: GridColDef = {
      field: "level",
      headerName: "Level",
      width: 72,
      sortable: true,
    };
    const selectedCols = catalog
      .filter((c) => activeLb.columns.includes(c.field))
      .map(_blankOnError);
    return [...alwaysCols, levelCol, ...selectedCols];
  };

  // ── Export current leaderboard to CSV ─────────────────────────────────────
  // Colored `*_p` percentile columns display an underlying raw stat (e.g.
  // wrcplus_p → row.wrcplus); export those raw values so the CSV is readable.
  const RAW_OVERRIDE: Record<string, string> = { velo_p: "max_velo" };
  const exportCsv = () => {
    if (!activeLb || activeLb.rows.length === 0) return;
    const catalog = activeLb.player_type === "hitter" ? hitter_columns_all : pitcher_columns_all;
    const labelOf = new Map(catalog.map((c) => [c.field, String(c.headerName ?? c.field)]));
    const idCols: { field: string; header: string }[] =
      activeLb.player_type === "hitter"
        ? [
            { field: "name", header: "Name" },
            { field: "MLB_AbbName", header: "Org" },
            { field: "Position", header: "Pos" },
            { field: "level", header: "Level" },
          ]
        : [
            { field: "name", header: "Name" },
            { field: "MLB_AbbName", header: "Org" },
            { field: "role", header: "Role" },
            { field: "level", header: "Level" },
          ];
    const statCols = activeLb.columns.map((f) => ({
      field: f.endsWith("_p") ? RAW_OVERRIDE[f] ?? f.slice(0, -2) : f,
      header: labelOf.get(f) ?? f,
    }));
    const cols = [...idCols, ...statCols];
    const cellValue = (row: Record<string, any>, field: string) => {
      const v = row[field];
      if (field === "MLB_AbbName") return v === "0" || v === 0 || v == null ? "FA" : String(v);
      return v;
    };
    const esc = (v: any) => {
      if (v === null || v === undefined) return "";
      let s = String(v);
      if (/[",\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.map((c) => esc(c.header)).join(",");
    const lines = activeLb.rows.map((row) =>
      cols.map((c) => esc(cellValue(row, c.field))).join(",")
    );
    const csv = [header, ...lines].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(activeLb.name || "leaderboard").replace(/[^\w-]+/g, "_")}_${activeLb.season}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const catalog = formPlayerType === "hitter" ? HITTER_CATALOG : PITCHER_CATALOG;
  const filteredCatalog = colFilter
    ? catalog.filter(
        (c) =>
          c.label.toLowerCase().includes(colFilter.toLowerCase()) ||
          c.field.toLowerCase().includes(colFilter.toLowerCase())
      )
    : catalog;

  // ── Gate: not signed in ───────────────────────────────────────────────────
  if (!user) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="text.secondary">
          Sign in with PS Plus to use Custom Leaderboards.
        </Typography>
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 1, sm: 2 }, py: 3 }}>
        <title>Custom Leaderboards - Prospect Savant</title>
      {/* ════════ LIST ════════ */}
      {mode === "list" && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
            <Typography variant="h5" fontWeight="bold" color={DARK}>
              My Leaderboards
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                backgroundColor: ACCENT,
                textTransform: "none",
                fontWeight: "bold",
                borderRadius: 2,
                "&:hover": { backgroundColor: "#2e4a6a" },
              }}
            >
              New Leaderboard
            </Button>
          </Box>

          {listLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
          {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

          {!listLoading && !listError && leaderboards.length === 0 && (
            <Paper
              elevation={0}
              sx={{ border: "1.5px solid #c8d8e8", borderRadius: 3, p: 5, textAlign: "center" }}
            >
              <TableChartIcon sx={{ fontSize: 52, color: "#c8d8e8", mb: 1.5 }} />
              <Typography color="text.secondary">
                No custom leaderboards yet. Create one to track any group of players!
              </Typography>
            </Paper>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {leaderboards.map((lb) => (
              <Paper
                key={lb.id}
                elevation={0}
                sx={{
                  border: "1.5px solid #c8d8e8",
                  borderRadius: 2,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  "&:hover": { borderColor: ACCENT },
                  transition: "border-color 0.15s",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight="bold" color={DARK} noWrap>
                    {lb.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {lb.player_type === "hitter" ? "Hitters" : "Pitchers"} ·{" "}
                    {lb.season} · {lb.player_ids.length} player
                    {lb.player_ids.length !== 1 ? "s" : ""} · {lb.columns.length} column
                    {lb.columns.length !== 1 ? "s" : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => openView(lb)}
                  sx={{ textTransform: "none", color: ACCENT, fontWeight: "bold" }}
                >
                  View
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => openEdit(lb)}
                  sx={{ textTransform: "none", color: ACCENT }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setDeleteConfirmId(lb.id)}
                  sx={{ color: "#e57373" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* ════════ CREATE / EDIT FORM ════════ */}
      {(mode === "create" || mode === "edit") && (
        <Box>
          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
            <IconButton onClick={() => setMode("list")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold" color={DARK}>
              {mode === "create" ? "New Leaderboard" : "Edit Leaderboard"}
            </Typography>
          </Box>

          {viewLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          )}

          {!viewLoading && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* ── Name ── */}
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.75}>
                  Leaderboard Name
                </Typography>
                <TextField
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Power Hitter Watchlist"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 60 }}
                  sx={{ maxWidth: 480 }}
                />
              </Box>

              {/* ── Type + Season ── */}
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-end" }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" mb={0.75}>
                    Player Type
                  </Typography>
                  <ToggleButtonGroup
                    value={formPlayerType}
                    exclusive
                    onChange={(_, v) => {
                      if (v) {
                        setFormPlayerType(v);
                        setFormColumns([]);
                      }
                    }}
                    size="small"
                  >
                    <ToggleButton value="hitter" sx={{ textTransform: "none", px: 2.5 }}>
                      Hitters
                    </ToggleButton>
                    <ToggleButton value="pitcher" sx={{ textTransform: "none", px: 2.5 }}>
                      Pitchers
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" mb={0.75}>
                    Season
                  </Typography>
                  <Select
                    size="small"
                    value={formSeason}
                    onChange={(e) => setFormSeason(e.target.value)}
                    sx={{ minWidth: 100 }}
                  >
                    {SEASONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              </Box>

              {/* ── Player picker ── */}
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.75}>
                  Players{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ({formPlayers.length} added)
                  </Typography>
                </Typography>

                <Box ref={searchContainerRef} sx={{ position: "relative", maxWidth: 480 }}>
                  <Paper
                    variant="outlined"
                    sx={{ display: "flex", alignItems: "center", px: 1.5, py: 0.6, borderRadius: 2 }}
                  >
                    <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: 18, flexShrink: 0 }} />
                    <InputBase
                      placeholder="Search for players to add…"
                      value={playerSearch}
                      onChange={(e) => setPlayerSearch(e.target.value)}
                      fullWidth
                      sx={{ fontSize: "0.9rem" }}
                    />
                  </Paper>

                  {searchOpen && !levelChoices && playerResults.length > 0 && (
                    <Paper
                      elevation={4}
                      sx={{
                        position: "absolute",
                        top: "calc(100% + 2px)",
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        maxHeight: 280,
                        overflowY: "auto",
                        borderRadius: 2,
                      }}
                    >
                      {playerResults.map((p) => (
                        <Box
                          key={p.id}
                          onClick={() => addPlayer(p)}
                          sx={{
                            px: 2,
                            py: 1.2,
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#f0f5fa" },
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {p.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {p.Position} · {p.MLB_AbbName ?? "FA"}
                          </Typography>
                        </Box>
                      ))}
                    </Paper>
                  )}

                  {/* ── Level chooser (player at multiple levels) ── */}
                  {levelChoices && (
                    <Paper
                      elevation={4}
                      sx={{
                        position: "absolute",
                        top: "calc(100% + 2px)",
                        left: 0,
                        right: 0,
                        zIndex: 20,
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          {levelChoices.player.name} played at multiple levels — add each one:
                        </Typography>
                        <IconButton size="small" onClick={closeSearch} sx={{ p: 0.25 }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {levelChoices.levels.map((lvl) => {
                          const already = formPlayers.some(
                            (fp) => entryKey(fp.id, fp.level) === entryKey(levelChoices.player.id, lvl)
                          );
                          return (
                            <Button
                              key={lvl}
                              size="small"
                              variant="outlined"
                              disabled={already}
                              onClick={() => addLevelChoice(lvl)}
                              sx={{ textTransform: "none", borderRadius: 2, minWidth: 56 }}
                            >
                              {already ? `${lvl} ✓` : lvl}
                            </Button>
                          );
                        })}
                      </Box>
                    </Paper>
                  )}
                </Box>

                {/* ── Bulk add: whole level / organization ── */}
                <Box
                  sx={{
                    mt: 1.5,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ width: "100%" }}>
                    Or add everyone from a level or organization at once:
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Select
                      size="small"
                      displayEmpty
                      value={bulkLevel}
                      onChange={(e) => setBulkLevel(e.target.value)}
                      sx={{ minWidth: 120 }}
                      renderValue={(v) => (v ? `Level: ${v}` : "Level…")}
                    >
                      <MenuItem value="" disabled>
                        Select a level
                      </MenuItem>
                      {bulkLevels.map((l) => (
                        <MenuItem key={l} value={l}>
                          {l}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!bulkLevel || bulkLoading !== ""}
                      onClick={() => addBulk("level", bulkLevel)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      {bulkLoading === "level" ? <CircularProgress size={16} /> : "Add level"}
                    </Button>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Select
                      size="small"
                      displayEmpty
                      value={bulkOrg}
                      onChange={(e) => setBulkOrg(e.target.value)}
                      sx={{ minWidth: 130 }}
                      renderValue={(v) => (v ? `Org: ${v}` : "Organization…")}
                    >
                      <MenuItem value="" disabled>
                        Select an organization
                      </MenuItem>
                      {bulkOrgs.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!bulkOrg || bulkLoading !== ""}
                      onClick={() => addBulk("org", bulkOrg)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      {bulkLoading === "org" ? <CircularProgress size={16} /> : "Add org"}
                    </Button>
                  </Box>
                  {bulkMsg && (
                    <Typography variant="caption" color="text.secondary" sx={{ width: "100%" }}>
                      {bulkMsg}
                    </Typography>
                  )}
                </Box>

                {formPlayers.length > 0 && (
                  <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {formPlayers.map((p) => {
                      const key = entryKey(p.id, p.level);
                      return (
                        <Chip
                          key={key}
                          label={p.level ? `${p.name} (${p.level})` : p.name}
                          onDelete={() => removePlayer(key)}
                          size="small"
                          sx={{ backgroundColor: "#e8f0f8" }}
                        />
                      );
                    })}
                  </Box>
                )}
              </Box>

              {/* ── Column picker ── */}
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={0.75}>
                  Columns{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ({formColumns.length} selected · Name & Org always shown)
                  </Typography>
                </Typography>

                <TextField
                  placeholder="Filter columns…"
                  size="small"
                  value={colFilter}
                  onChange={(e) => setColFilter(e.target.value)}
                  sx={{ mb: 1.5, width: 240 }}
                  inputProps={{ style: { fontSize: "0.85rem" } }}
                />

                <Paper
                  variant="outlined"
                  sx={{ maxHeight: 320, overflowY: "auto", p: 1.5, borderRadius: 2 }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                    {filteredCatalog.map((c) => (
                      <FormControlLabel
                        key={c.field}
                        control={
                          <Checkbox
                            size="small"
                            checked={formColumns.includes(c.field)}
                            onChange={() => toggleCol(c.field)}
                          />
                        }
                        label={<Typography variant="body2">{c.label}</Typography>}
                        sx={{ width: { xs: "50%", sm: "33%" }, m: 0 }}
                      />
                    ))}
                    {filteredCatalog.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                        No columns match "{colFilter}"
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>

              {saveError && <Alert severity="error">{saveError}</Alert>}

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    backgroundColor: ACCENT,
                    textTransform: "none",
                    fontWeight: "bold",
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "#2e4a6a" },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={18} sx={{ color: "#fff" }} />
                  ) : mode === "create" ? (
                    "Create Leaderboard"
                  ) : (
                    "Save Changes"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setMode("list")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* ════════ VIEW ════════ */}
      {mode === "view" && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <IconButton onClick={() => setMode("list")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold" color={DARK} sx={{ flex: 1 }}>
              {activeLb?.name ?? ""}
            </Typography>
            {activeLb && (
              <>
                <Button
                  size="small"
                  startIcon={<FilterAltIcon fontSize="small" />}
                  onClick={() => setHeaderFilters((on) => !on)}
                  sx={{
                    textTransform: "none",
                    color: ACCENT,
                    backgroundColor: headerFilters ? "rgba(61,90,128,0.12)" : undefined,
                  }}
                >
                  Filter Row
                </Button>
                <Button
                  size="small"
                  startIcon={<FileDownloadIcon fontSize="small" />}
                  onClick={exportCsv}
                  disabled={activeLb.rows.length === 0}
                  sx={{ textTransform: "none", color: ACCENT }}
                >
                  Export CSV
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={() => openEdit(activeLb)}
                  sx={{ textTransform: "none", color: ACCENT }}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  onClick={() => setDeleteConfirmId(activeLb.id)}
                  sx={{ color: "#e57373" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>

          {activeLb && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {activeLb.player_type === "hitter" ? "Hitters" : "Pitchers"} · {activeLb.season} ·{" "}
              {activeLb.rows.length} player{activeLb.rows.length !== 1 ? "s" : ""}
            </Typography>
          )}

          {viewLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          )}
          {viewError && <Alert severity="error">{viewError}</Alert>}

          {!viewLoading && activeLb && (
            <>
              {activeLb.rows.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{ border: "1.5px solid #c8d8e8", borderRadius: 3, p: 5, textAlign: "center" }}
                >
                  <Typography color="text.secondary">
                    No player data found for the selected players and season.
                  </Typography>
                </Paper>
              ) : (
                <Box sx={{ height: "calc(100vh - 300px)", minHeight: 400 }}>
                  <StripedDataGrid
                    rows={activeLb.rows}
                    columns={buildGridColumns()}
                    getRowId={(row) => row._rowKey ?? row.id}
                    getRowClassName={(params) =>
                      params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                    }
                    disableRowSelectionOnClick
                    density="compact"
                    headerFilters={headerFilters}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* ════════ DELETE CONFIRM DIALOG ════════ */}
      <Dialog open={deleteConfirmId !== null} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Leaderboard</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this leaderboard? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => deleteConfirmId !== null && handleDelete(deleteConfirmId)}
            disabled={deleting}
            sx={{ textTransform: "none", color: "#e57373" }}
          >
            {deleting ? <CircularProgress size={16} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
